import { DOCUMENT, Injector, Service, inject, signal } from '@angular/core';
import { Router, TitleStrategy } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { SaphirLanguageContext } from '@saphir/module-contract';
import { PrimeNG } from 'primeng/config';
import { Observable, firstValueFrom, tap } from 'rxjs';

import {
  DEFAULT_LANGUAGE,
  LANGUAGE_STORAGE_KEY,
  LanguageCode,
  PRIMENG_TRANSLATION_KEY,
  isSupportedLanguage,
  toSupportedLanguage,
} from '../core/i18n/language';

/**
 * Owns the active UI language, and the implementation behind `SAPHIR_LANGUAGE`.
 *
 * The language chosen last wins (the header switcher, remembered in local
 * storage like the UMC does it — user decision 2026-09-23); a visitor who has
 * not chosen yet gets the browser's own preference.
 *
 * Shell code and modules read `current()` instead of talking to
 * `TranslateService`, so the side effects stay in one place.
 */
@Service()
export class LanguageService implements SaphirLanguageContext {
  private readonly translate = inject(TranslateService);
  private readonly primeng = inject(PrimeNG);
  private readonly document = inject(DOCUMENT);
  private readonly injector = inject(Injector);

  private readonly active = signal<LanguageCode>(DEFAULT_LANGUAGE);

  /** Currently active language code. Named for the contract it satisfies. */
  readonly current = this.active.asReadonly();

  private ready?: Promise<void>;

  /**
   * Resolves the browser language and loads its dictionary. Called from an app
   * initializer, so the first render already has translations and never
   * flashes raw keys.
   *
   * Idempotent, and returns the same promise every time: the module registry
   * has to know the language before it merges module dictionaries, and app
   * initializers run concurrently — awaiting this is how that startup step
   * states its dependency instead of relying on provider order.
   */
  initialize(): Promise<void> {
    this.ready ??= firstValueFrom(
      this.applyLanguage(this.storedLanguage() ?? this.resolveBrowserLanguage()),
    ).then(() => undefined);

    return this.ready;
  }

  /**
   * Switches the language at runtime and remembers the choice. Every
   * translated string is read through ngx-translate or `current()`, so the
   * whole application re-renders without a reload — modules included, whose
   * dictionaries are merged for every shipped language at startup.
   */
  async change(language: LanguageCode): Promise<void> {
    if (language === this.active()) {
      return;
    }

    await firstValueFrom(this.applyLanguage(language));
    this.remember(language);

    // The browser tab title was rendered on the last navigation; say it again
    // in the new language. Taken lazily: the router does not exist yet when the
    // app initializer creates this service.
    const router = this.injector.get(Router, null);

    if (router) {
      this.injector.get(TitleStrategy, null)?.updateTitle(router.routerState.snapshot);
    }
  }

  private applyLanguage(language: LanguageCode): Observable<unknown> {
    return this.translate.use(language).pipe(
      tap(() => {
        this.active.set(language);
        this.document.documentElement.lang = language;
        this.syncPrimeNgTranslation();
      }),
    );
  }

  /**
   * Feeds the `primeng` subtree of the dictionary into PrimeNG so its built-in
   * labels follow the app language. A missing subtree is not an error —
   * PrimeNG keeps its English defaults.
   */
  private syncPrimeNgTranslation(): void {
    const translation = this.translate.instant(PRIMENG_TRANSLATION_KEY);

    if (translation && typeof translation === 'object') {
      this.primeng.setTranslation(translation);
    }
  }

  /** The language chosen last, if the browser lets us read it back. */
  private storedLanguage(): LanguageCode | null {
    try {
      const stored = this.document.defaultView?.localStorage?.getItem(LANGUAGE_STORAGE_KEY);

      return isSupportedLanguage(stored) ? stored : null;
    } catch {
      return null;
    }
  }

  // Storage is unavailable in private windows and behind some policies. Losing
  // the preference is a small thing; failing to switch the language is not.
  private remember(language: LanguageCode): void {
    try {
      this.document.defaultView?.localStorage?.setItem(LANGUAGE_STORAGE_KEY, language);
    } catch {
      // Ignored on purpose — see above.
    }
  }

  /**
   * Only the browser's *primary* preference decides.
   *
   * Deliberately not a walk down `navigator.languages`: a German Windows or a
   * German Chrome build leaves `de` further down the list even when the user
   * has explicitly put another language first, so walking it would serve German
   * to someone who asked for Russian. An unsupported first choice means we know
   * nothing useful about the user — fall back to English.
   */
  private resolveBrowserLanguage(): LanguageCode {
    const navigator = this.document.defaultView?.navigator;
    const primary = navigator?.languages?.[0] ?? navigator?.language;

    return toSupportedLanguage(primary) ?? DEFAULT_LANGUAGE;
  }
}
